/**
 * Data Export Service
 * 
 * Provides functionality to export all user data for GDPR compliance
 * (Article 15 - Right of Access, Article 20 - Right to Data Portability)
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/functions';
import { Alert, Linking, Platform } from 'react-native';
import * as FileSystem from '@/lib/fileSystemLegacy';
import * as Sharing from 'expo-sharing';

export type ExportUserDataResponse = {
  success: boolean;
  downloadUrl?: string;
  expiresAt?: string;
  message?: string;
};

/**
 * Export all user data
 * Returns a download URL that expires in 7 days
 */
export const exportUserData = async (): Promise<ExportUserDataResponse> => {
  try {
    const exportFunction = httpsCallable<{}, ExportUserDataResponse>(
      functions,
      'exportUserData'
    );

    const result = await exportFunction({});
    const data = result.data;

    if (!data.success || !data.downloadUrl) {
      throw new Error(data.message || 'Failed to export data');
    }

    return data;
  } catch (error: any) {
    console.error('[dataExport] Error exporting user data:', error);
    throw new Error(
      error.message || 'Failed to export your data. Please try again or contact support.'
    );
  }
};

/**
 * Download and save the exported data file
 */
export const downloadExportedData = async (downloadUrl: string): Promise<void> => {
  try {
    // Web platform: open in new tab
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined') {
        window.open(downloadUrl, '_blank');
        Alert.alert('Export Successful', 'Your data export has been opened in a new tab. You can save it from there.');
      }
      return;
    }

    // Native platforms: download and share
    const fileName = `tripvio-data-export-${new Date().toISOString().split('T')[0]}.json`;
    const documentDir = FileSystem.documentDirectory;
    
    if (!documentDir) {
      throw new Error('Document directory not available');
    }

    const fileUri = `${documentDir}${fileName}`;

    // Download the file
    const downloadResult = await FileSystem.downloadAsync(downloadUrl, fileUri);
    
    if (downloadResult.status !== 200) {
      throw new Error('Failed to download export file');
    }

    // Share the file
    const isAvailable = await Sharing.isAvailableAsync();
    if (isAvailable) {
      await Sharing.shareAsync(fileUri, {
        mimeType: 'application/json',
        dialogTitle: 'Share your Tripvio data export',
      });
    } else {
      // Fallback: show alert with file location
      Alert.alert(
        'Export Successful',
        `Your data export has been saved to:\n${fileUri}\n\nYou can access it from your device's file manager.`,
        [{ text: 'OK' }]
      );
    }
  } catch (error: any) {
    console.error('[dataExport] Error downloading exported data:', error);
    throw new Error(
      error.message || 'Failed to download your data export. Please try again.'
    );
  }
};

/**
 * Export user data and handle the download
 */
export const exportAndDownloadUserData = async (): Promise<void> => {
  try {
    // Show loading alert
    Alert.alert(
      'Exporting Your Data',
      'This may take a few moments. Please wait...',
      [],
      { cancelable: false }
    );

    // Export data
    const result = await exportUserData();

    if (!result.downloadUrl) {
      throw new Error('No download URL received');
    }

    // Download the file
    await downloadExportedData(result.downloadUrl);

    // Show success message
    Alert.alert(
      'Export Successful',
      `Your data export is ready. The download link expires on ${result.expiresAt ? new Date(result.expiresAt).toLocaleDateString() : 'in 7 days'}.\n\n${result.message || ''}`,
      [{ text: 'OK' }]
    );
  } catch (error: any) {
    console.error('[dataExport] Error in export and download:', error);
    Alert.alert(
      'Export Failed',
      error.message || 'Failed to export your data. Please try again or contact support at privacy@tripvio.app.',
      [{ text: 'OK' }]
    );
    throw error;
  }
};

